import { useQuery } from "@tanstack/react-query";
import { Check, Loader2, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useCheckout } from "@/lib/hooks";
import { isMobile } from "@/lib/config";
import { money } from "@/lib/format";
import { BillingInterval } from "@/lib/types";
import { cn } from "@/lib/utils";

export const membershipFeatures = [
  "Full segmented video library",
  "AI Deal Analyzer access",
  "Resource hub downloads",
  "Required baseline for all scheme tiers",
];

/** Shown instead of every Buy/Subscribe/Cancel action in the mobile app. */
export function ManageOnWebsite({ className, children }: { className?: string; children?: React.ReactNode }) {
  return (
    <div className={cn("flex items-start gap-3 rounded-md border border-border bg-secondary px-4 py-3 text-sm", className)}>
      <Smartphone className="mt-0.5 size-4 shrink-0 text-gold" />
      <p className="text-muted-foreground">{children ?? "Manage your membership on our website."}</p>
    </div>
  );
}

/** The membership plan card (template "Academy Portal" pricing card) with a Subscribe button. */
export function MembershipPlanCard({ title = "Subscription required", className }: { title?: string; className?: string }) {
  const plan = useQuery({ queryKey: ["billing", "plan"], queryFn: api.billing.plan });
  const checkout = useCheckout();
  const p = plan.data;

  return (
    <div className={cn("mx-auto max-w-lg rounded-lg border border-gold bg-card p-9 text-center shadow-elevated", className)}>
      <p className="eyebrow text-gold">{title}</p>
      <p className="mt-3 font-display text-3xl">{p?.name ?? "Academy Membership"}</p>
      {p && !isMobile && (
        <>
          <p className="mt-4 font-display text-6xl">{money(p.price, p.currency)}</p>
          <p className="text-sm text-muted-foreground">
            per {p.billingInterval === BillingInterval.Yearly ? "year" : "month"}, incl. UK VAT · cancel anytime
          </p>
        </>
      )}
      {p?.description && <p className="mt-4 text-sm text-muted-foreground">{p.description}</p>}
      <div className="mt-7 space-y-3 text-left">
        {membershipFeatures.map((f) => (
          <div key={f} className="flex items-start gap-3 text-sm">
            <Check className="mt-0.5 size-4 shrink-0 text-gold" />
            {f}
          </div>
        ))}
      </div>
      {isMobile ? (
        <ManageOnWebsite className="mt-8 text-left" />
      ) : (
        <Button
          variant="gold"
          size="xl"
          className="mt-8 w-full"
          disabled={checkout.isPending || plan.isPending || !p}
          onClick={() => checkout.mutate({ kind: "subscription" })}
        >
          {checkout.isPending && <Loader2 className="animate-spin" />}
          Subscribe{p ? ` — ${money(p.price, p.currency)}/${p.billingInterval === BillingInterval.Yearly ? "yr" : "mo"}` : ""}
        </Button>
      )}
    </div>
  );
}
